import {
  mirrorCenterX,
  mirrorAnchorX,
  type Rect,
} from "@yjsgames/phaser-ui/panel-content";
import type { TextDirection } from "@yjsgames/i18n";
import { computeLabelButtonRow } from "./tabRowLayout.js";
import spec from "../data/layouts/dailyShop.json";

/** 헤더 아래 팁 캐러셀 영역 — 높이 40, 위 간격 6 (스탯/합성 탭 동일). 그리드가 그만큼 아래로 밀린다. */
const TIP_H = 40;
const TIP_GAP = 6;

export interface DailyShopCellLayout {
  /** 셀 배경 패널 rect (pad/2 inset, origin 0,0). */
  readonly bg: Rect;
  readonly icon: { x: number; y: number };
  readonly name: { x: number; y: number };
  readonly detail: { x: number; y: number; wrapWidth: number };
  /** 구매 버튼 중심 + 폭(셀 폭 가변) + 높이(셀 frame 안 배치 계산용). */
  readonly buyBtn: { x: number; y: number; width: number; height: number };
  /** 할인 배지 앵커 (LTR 우상단 / RTL 좌상단). origin은 view가 dir로 결정. */
  readonly discountBadge: { x: number; y: number };
}

export interface DailyShopLayout {
  readonly textOriginX: number;
  /** 셀 내부 텍스트(이름/상세) origin x. */
  readonly cellTextOriginX: number;
  readonly title: { x: number; y: number };
  readonly countdown: { x: number; y: number };
  readonly rerollBtn: { x: number; y: number };
  /** 헤더 아래 팁 캐러셀 영역 (전체폭) — TipCarouselView 배치 기준(스탯 탭 동형). */
  readonly tip: Rect;
  /** 고정 셀 그리드를 담는 스크롤 영역. 셀 좌표는 이 영역의 로컬 좌표다. */
  readonly scroll: Rect;
  readonly cells: readonly DailyShopCellLayout[];
  readonly contentHeight: number;
  readonly result: { x: number; y: number };
}

/**
 * 일일상점 탭 좌표 — 헤더 offset + 2×3 grid 수치 + 셀 내부 5요소 offset은 dailyShop.json 선언,
 * RTL 열 미러·셀 내부 패딩 미러는 기존 mirror 유틸 유지. bottomY로 그리드 세로(셀 높이) 도출.
 */
export function computeDailyShopLayout(
  content: Rect,
  dir: TextDirection,
  bottomY: number,
  slotCount: number,
): DailyShopLayout {
  const head = computeLabelButtonRow(content.x, content.width, spec.rerollButtonInset, dir);
  // 팁 캐러셀은 헤더(title/countdown/reroll) 아래에 두고, 그리드는 (TIP_H+TIP_GAP)만큼 더 아래로 민다.
  const tipY = content.y + spec.headerH;
  const scrollTop = tipY + TIP_H + TIP_GAP;
  const scrollH = Math.max(0, bottomY - scrollTop - spec.resultH);
  const contentPadX = spec.discountBadgeOutset;
  const gridW = content.width - spec.scrollBarReserve - contentPadX * 2;
  const cellW = Math.floor(gridW / spec.cols);
  const cellH = spec.cellHeight;
  const padX = spec.pad;
  const padY = spec.padY;
  const contentPadY = spec.contentPadY;
  const rowGap = spec.rowGap;
  const contentHeight = contentPadY * 2 + spec.rows * cellH + (spec.rows - 1) * rowGap;

  const cells: DailyShopCellLayout[] = [];
  for (let i = 0; i < slotCount; i++) {
    const col = i % spec.cols;
    const row = Math.floor(i / spec.cols);
    const mcol = dir === "rtl" ? spec.cols - 1 - col : col;
    const cellX = contentPadX + mcol * cellW;
    const cellY = contentPadY + row * (cellH + rowGap);
    const bg = {
      x: cellX + padX / 2,
      y: cellY + padY / 2,
      width: cellW - padX,
      height: cellH - padY,
    };
    cells.push({
      bg,
      icon: { x: cellX + mirrorCenterX(padX + spec.iconDx, cellW, dir), y: cellY + padY + spec.iconDy },
      name: { x: cellX + mirrorCenterX(padX + spec.nameDx, cellW, dir), y: cellY + padY + spec.nameDy },
      detail: {
        x: cellX + mirrorCenterX(padX + spec.detailDx, cellW, dir),
        y: cellY + padY + spec.detailDy,
        wrapWidth: cellW - padX - spec.detailWrapInset,
      },
      buyBtn: {
        x: cellX + cellW / 2,
        y: bg.y + bg.height - spec.buyBtnHeight / 2 - spec.buyBtnBottomMargin,
        width: cellW - spec.buyBtnWidthInset,
        height: spec.buyBtnHeight,
      },
      discountBadge: {
        x:
          dir === "rtl"
            ? bg.x - spec.discountBadgeOutset
            : bg.x + bg.width + spec.discountBadgeOutset,
        y: bg.y - spec.discountBadgeOutset,
      },
    });
  }

  return {
    textOriginX: head.textOriginX,
    cellTextOriginX: mirrorAnchorX(0, dir),
    title: { x: head.textX, y: content.y + spec.title },
    countdown: { x: head.textX, y: content.y + spec.countdown },
    rerollBtn: { x: head.buttonX, y: content.y + spec.reroll },
    tip: { x: content.x, y: tipY, width: content.width, height: TIP_H },
    scroll: { x: content.x, y: scrollTop, width: content.width, height: scrollH },
    cells,
    contentHeight,
    result: { x: head.textX, y: bottomY - spec.resultH + 4 },
  };
}
